import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  inject,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { GenericSelectorComponent } from '@root/src/app/pages/Ui-Kit/generic-selector/generic-selector.component';
import { GenericSelectorConfig } from '@root/src/app/pages/Ui-Kit/generic-selector/generic-selector.models';
import { FieldService } from '@6grain/masofa-monolith-api-client/crop-monitoring/api/field.service';
import { SeasonService } from '@6grain/masofa-monolith-api-client/crop-monitoring/api/season.service';
import { CropService } from '@6grain/masofa-monolith-api-client/dictionaries/api/crop.service';
import { Field } from '@6grain/masofa-monolith-api-client/crop-monitoring/model/field';
import { Crop } from '@6grain/masofa-monolith-api-client/dictionaries/model/crop';
import { SortType } from '@6grain/masofa-monolith-api-client/uav/model/sortType';
import { FilterOperator } from '@root/src/app/utils/filter-operator';
import { ServerTranslatePipe } from '@app/utils/pipes/server-translate-pipe';

@Component({
  selector: 'app-uav-add-field-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    GenericSelectorComponent,
    ServerTranslatePipe,
  ],
  templateUrl: './uav-add-field-modal.component.html',
  styleUrls: ['./uav-add-field-modal.component.scss'],
})
export class UavAddFieldModalComponent implements OnInit, OnDestroy {
  @Input() isVisible: boolean = false;
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<any>();

  addFieldForm!: FormGroup;
  availableCrops: Crop[] = [];

  private allCropsDictionary: Crop[] = [];
  private destroy$ = new Subject<void>();
  private fieldService = inject(FieldService);
  private seasonService = inject(SeasonService);
  private cropService = inject(CropService);

  constructor(private fb: FormBuilder) {}

  fieldSelectorConfig: GenericSelectorConfig<Field> = {
    searchField: 'Name',
    displayField: 'name',
    idField: 'id',
    getItems: (params) =>
      this.fieldService.cropMonitoringFieldGetByQueryPost({
        offset: params.offset,
        take: params.take,
        filters: params.filters,
        sortBy: 'Name',
        sort: SortType.NUMBER_0,
      }),
    getTotalCount: (params) =>
      this.fieldService.cropMonitoringFieldGetTotalCountPost({
        filters: params.filters,
      }),
  };

  ngOnInit(): void {
    this.addFieldForm = this.fb.group({
      field: [null, Validators.required],
      culture: [null],
    });

    this.loadAllCrops();
    this.addFieldForm
      .get('field')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((val: any) => {
        this.addFieldForm.patchValue({ culture: null });
        this.availableCrops = [];
        const fieldId = val && typeof val === 'object' ? val.id : val;
        if (fieldId) {
          this.loadSeasonsForField(fieldId);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadAllCrops(): void {
    this.cropService.dictionariesCropGetByQueryPost({ take: 999 }).subscribe({
      next: (res) => {
        this.allCropsDictionary = res || [];
      },
      error: (err) => console.error('Ошибка загрузки справочника культур', err),
    });
  }

  private loadSeasonsForField(fieldId: string): void {
    this.seasonService
      .cropMonitoringSeasonGetByQueryPost({
        filters: [
          {
            filterField: 'FieldId',
            filterOperator: FilterOperator.Equals,
            filterValue: fieldId,
          },
        ],
      })
      .subscribe({
        next: (seasons) => {
          if (!seasons || seasons.length === 0) {
            this.availableCrops = [];
            return;
          }

          const uniqueCropIds = [
            ...new Set(seasons.map((s) => s.cropId).filter((id) => !!id)),
          ];

          this.availableCrops = this.allCropsDictionary.filter((c) =>
            uniqueCropIds.includes(c.id)
          );
        },
        error: (err) => console.error('Ошибка загрузки сезонов', err),
      });
  }

  onClose(): void {
    this.close.emit();
    this.addFieldForm.reset();
    this.availableCrops = [];
  }

  onSaveSubmit(): void {
    if (this.addFieldForm.valid) {
      this.save.emit(this.addFieldForm.value);
    } else {
      this.addFieldForm.markAllAsTouched();
    }
  }

  stopPropagation(event: Event): void {
    event.stopPropagation();
  }
}
